import React, { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Text, TextLink, Stack } from 'braid-design-system';

import {
  JobTitle,
  type JobTitleChangeHandler,
  type JobTitleChangeEvent,
} from 'src/App/components/JobTitle/JobTitle';
import { JobLocation } from '@seek/job-location';
import { TrackingContext } from 'src/App/context/trackingContext';
import type { JobLocationChangeHandler } from '@seek/job-location';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { JobCategory, type JobCategoryChangeHandler } from '@seek/job-category';
import { useAppContext } from 'src/App/context/AppContext';
import { type Locale, getLanguageFromLocale } from '@seek/melways-sites';
import { usePricingContext } from 'src/App/context/PricingContextProvider';
import { useMelwaysLink } from '@seek/melways-react';

interface PriceLookUpProps {
  onJobTitleChange: JobTitleChangeHandler;
  onLocationChange: JobLocationChangeHandler;
  onCategoryChange: JobCategoryChangeHandler;
  advertiserId?: string;
}

const PriceLookUpInputs = ({
  onJobTitleChange,
  onLocationChange,
  onCategoryChange,
  advertiserId,
}: PriceLookUpProps) => {
  const { t } = useTranslations(translations);
  const [jobTitle, setJobTitle] = useState('');
  const [debouncedJobTitle] = useDebounce(jobTitle, 1000);
  const tracking = React.useContext(TrackingContext);
  const { language: locale } = useAppContext();
  const language = getLanguageFromLocale(locale as Locale);
  const { isOnVariablePricing } = usePricingContext();
  const jobAdsPath = useMelwaysLink({ path: '/products/jobads' });

  const onJobTitleChangeInternal = (event: JobTitleChangeEvent): void => {
    if (event.isValid) {
      setJobTitle(event.value);
    }
    onJobTitleChange(event);
  };

  return (
    <Stack space={'large'}>
      <Stack space={'small'}>
        {isOnVariablePricing && (
          <Text>{t('ad prices may vary depending on...')}</Text>
        )}
        <Text>
          {t('For more information on ad types...', {
            TextLink: (v) => (
              <TextLink
                href={jobAdsPath}
                onClick={() => tracking.onProductLinkClicked()}
                target="_blank"
              >
                {v}
              </TextLink>
            ),
          })}
        </Text>
      </Stack>
      <JobTitle
        id="jobTitle"
        label={t('Title')}
        isValidationRequested={true}
        onChange={onJobTitleChangeInternal}
      />
      <JobCategory
        jobTitle={debouncedJobTitle}
        onCategoryChange={onCategoryChange}
        onSubCategoryChange={onCategoryChange}
        onSuggestedCategoryChange={onCategoryChange}
        language={language}
      />
      <JobLocation
        id="JobLocation"
        advertiserId={advertiserId}
        label={t('Location')}
        secondaryLabel={t('suburb or postcode')}
        onChange={onLocationChange}
        language={language}
      />
    </Stack>
  );
};

export { PriceLookUpInputs };
